<script setup>
import { ref, watch, computed } from 'vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import Dropdown from 'primevue/dropdown'
import { getAllRoles } from '@/modules/admin/roles/api/roles.service'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  user: {
    type: Object,
    default: null
  },
  submitting: {
    type: Boolean,
    default: false
  },
  error: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['close', 'submit', 'clear-error'])

const roleInput = ref('')
const roleOptions = ref([])
const rolesLoading = ref(false)
const rolesLoadError = ref('')

const currentRole = computed(() => {
  return props.user?.roles?.[0] || '—'
})

function normalizeRoleName(role) {
  return typeof role === 'string' ? role : role?.name
}

async function loadRoleOptions() {
  rolesLoading.value = true
  rolesLoadError.value = ''

  try {
    const roles = await getAllRoles()
    const currentRoleName = currentRole.value === '—' ? '' : currentRole.value
    const names = roles
      .map(normalizeRoleName)
      .filter((name) => name && name !== 'God')

    if (currentRoleName && currentRoleName !== 'God' && !names.includes(currentRoleName)) {
      names.push(currentRoleName)
    }

    roleOptions.value = [...new Set(names)]
  } catch (error) {
    rolesLoadError.value = error.message || 'Не удалось загрузить список ролей'
    roleOptions.value = []
  } finally {
    rolesLoading.value = false
  }
}

watch(
  () => props.visible,
  (value) => {
    if (value) {
      roleInput.value = currentRole.value === 'God' ? '' : currentRole.value
      loadRoleOptions()
    } else {
      roleInput.value = ''
      roleOptions.value = []
      rolesLoadError.value = ''
    }
  }
)

function closeDialog() {
  if (props.submitting) return
  emit('close')
}

function submitForm() {
  const roleName = roleInput.value?.trim()

  if (!roleName) {
    emit('clear-error')
    return
  }

  emit('submit', roleName)
}
</script>

<template>
  <Dialog
    :visible="props.visible"
    modal
    header="Изменить роль пользователя"
    :style="{ width: 'min(500px, 95vw)' }"
    class="change-role-dialog"
    :closable="!props.submitting"
    @update:visible="closeDialog"
  >
    <div class="form-container">
      <div class="user-info">
        <span class="label">Пользователь</span>
        <span class="value">{{ props.user?.email || props.user?.fullName || '—' }}</span>
      </div>

      <div class="user-info">
        <span class="label">Текущая роль</span>
        <span class="value">{{ currentRole }}</span>
      </div>

      <div class="field">
        <label>Новая роль</label>

        <Dropdown
          v-model="roleInput"
          :options="roleOptions"
          editable
          placeholder="Введите или выберите роль"
          class="w-full"
          :invalid="Boolean(props.error)"
          :disabled="props.submitting || rolesLoading"
          :loading="rolesLoading"
          @input="emit('clear-error')"
          @change="emit('clear-error')"
        />

        <small v-if="rolesLoadError" class="field-hint field-hint--error">
          {{ rolesLoadError }}
        </small>

        <small v-if="props.error" class="field-error">
          {{ props.error }}
        </small>
      </div>
    </div>

    <template #footer>
      <Button
        label="Отмена"
        class="cancel-btn"
        outlined
        :disabled="props.submitting"
        @click="closeDialog"
      />

      <Button
        label="Сохранить"
        icon="pi pi-check"
        class="submit-btn"
        :loading="props.submitting"
        :disabled="props.submitting || !roleInput.trim()"
        @click="submitForm"
      />
    </template>
  </Dialog>
</template>

<style scoped>
.form-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 6px;
}

.user-info,
.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.label,
.field label {
  font-size: 13px;
  font-weight: 600;
  color: #444444;
}

.value {
  font-size: 15px;
  color: #111111;
}

.field-error {
  font-size: 12px;
  color: #dc2626;
}

.field-hint {
  font-size: 12px;
  color: #777777;
}

.field-hint--error {
  color: #dc2626;
}

:deep(.p-dropdown) {
  width: 100%;
  border-radius: 14px;
  border-color: #dddddd;
  box-shadow: none;
}

:deep(.p-dropdown.p-invalid) {
  border-color: #dc2626;
}

:deep(.p-dropdown:not(.p-disabled).p-focus) {
  border-color: #111111;
  box-shadow: 0 0 0 3px rgba(196, 18, 23, 0.08);
}

:deep(.cancel-btn.p-button) {
  background: #ffffff;
  border: 1px solid #d8d8d8;
  color: #111111;
  border-radius: 999px;
}

:deep(.submit-btn.p-button) {
  background: #111111;
  border: 1px solid #111111;
  color: #ffffff;
  border-radius: 999px;
}
</style>
